/* ============================================================================
   FRONT LINE DEV - DO NOT EDIT
   This file powers Infinit's custom Quiz and Custom Formula functionality and
   is maintained exclusively by Front Line Dev. Editing it may break the
   formula builder, quiz, saved formulas, or related features. Please contact
   Front Line Dev for any changes.
   ============================================================================ */

/**
 * Quiz Scoring Engine
 *
 * Transforms raw quiz answers into personalized formula configurations
 * compatible with FormulaUrlHash.encode().
 *
 * Produces:
 *   - 1–2 Custom Hydration Formulas (A = short mix, B = long mix if 3+ hr)
 *   - 1 Custom Protein Formula
 *   - Product recommendations (Mocha Protein, NOCTURNE, Nutrition Coaching, Premium Fuel)
 *   - A Premium Fuel formula for Middle of the Pack, Competitive and Elite athletes
 *   - Personalized results text
 */
window.QuizEngine = (function () {
  'use strict';

  // ─── LOOKUP TABLES ───────────────────────────────────────────────

  // q5 sweat level → base electrolyte slider
  var SWEAT_ELECTROLYTE = { 0: 20, 25: 25, 50: 35, 75: 45, 100: 55 };

  // q7 salt → electrolyte adjustment
  var SALT_ADJ = { 0: 0, 25: 5, 50: 10, 75: 20, 100: 30 };

  // q8 cramp → electrolyte adjustment
  var CRAMP_ADJ = { 0: 0, 25: 0, 50: 0, 75: 5, 100: 10 };

  // q15 region → electrolyte adjustment (nullified when workout_location = inside)
  // AUS asks for the state (Nathan, 2026-10-12), but the adjustment is still
  // set by the three climate groups Stacy approved: hot north +10, temperate
  // south-east and south-west +5, cool south 0. The group keys are kept so
  // results saved before the per-state answers still score the same.
  var REGION_ADJ = {
    qld: 10,
    nt: 10,
    wa_north: 10,
    nsw: 5,
    act: 5,
    sa: 5,
    wa_south: 5,
    vic: 0,
    tas: 0,
    nz: 0,
    midwest: 5,
    northeast: 0,
    southeast: 10,
    southwest: 10,
    mountainwest: 5,
    northwest: 0,
    pacific_islands: 10,
    caribbean: 10
  };

  // q2 hunger (3+ hr) → Formula B protein_slider + aminoacids_slider
  var HUNGER_PROTEIN = { 0: { ps: 10, aa: 10 }, 33: { ps: 20, aa: 20 }, 66: { ps: 30, aa: 30 }, 100: { ps: 40, aa: 40 } };

  // Same but when ultra-marathon is selected
  var HUNGER_ULTRA = { 0: { ps: 10, aa: 10 }, 33: { ps: 10, aa: 10 }, 66: { ps: 20, aa: 20 }, 100: { ps: 20, aa: 20 } };

  // q12 body fat category → percentage used for LBW
  var BODY_FAT_MALE = { lt10: 10, '10_15': 12.5, '15_25': 20, gt25: 25 };
  var BODY_FAT_FEMALE = { lt15: 15, '15_25': 20, '25_35': 30, gt35: 35 };

  // q14 body type vibe → inferred body fat %
  var BODY_TYPE_MALE = { chiseled: 10, lean: 12.5, average: 20, could_lose: 25 };
  var BODY_TYPE_FEMALE = { chiseled: 15, lean: 20, average: 30, could_lose: 35 };

  // q18 flavor vibe → hydration flavors
  var FLAVOR_MAP = {
    // AUS (TW 40087473): citrus is Lemon Lime; berry is Watermelon or Raspberry.
    // Strawberry Lemonade and Fruit Punch are retired from AUS Custom Hydration.
    bright_citrusy: { a: 'Lemon Lime',          b: 'Orange' },
    fruit_berry:    { a: 'Watermelon',          b: 'Raspberry' },
    // Vanilla is retired from AUS Custom Hydration (Stacy, TW 40087473).
    creamy_cozy:    { a: 'Salted Caramel',      b: 'Chocolate' },
    unflavored:     { a: 'Unflavored',          b: 'Unflavored' }
  };

  // q19 caffeine preference → slider values per formula
  // (caffeine mg = slider x 2). "more" and "decent" were added for AUS (TW
  // 40087473) and sit evenly between Light Buzz and Fully Caffeinated.
  var CAFFEINE_MAP = {
    none:     { a: 0,  b: 0 },
    moderate: { a: 20, b: 10 },
    more:     { a: 23, b: 12 },
    decent:   { a: 27, b: 13 },
    high:     { a: 30, b: 15 },
    unknown:  { a: 0,  b: 0 }
  };

  // ── Premium Fuel (AUS, Stacy's answers on TW 40087473) ──
  // Values are the product's option labels, so they select directly.
  // q4: Middle of the Pack / Competitive / Elite. Middle of the Pack was added
  // for AUS (Nathan, 2026-10-12) and takes the lightest mix.
  var PF_FRUCTOSE = { 50: '75g Mix', 75: '90g Mix', 100: '120g Mix' };
  // Middle of the Pack goes by body weight (Nathan, 2026-10-13): 80kg and under
  // takes the 75g Mix, over 80kg the 90g Mix.
  var PF_MIDDLE_HEAVY_KG = 80;
  var PF_MIDDLE_HEAVY = '90g Mix';
  var PF_CAFFEINE = { none: '0mg', moderate: '25mg', more: '50mg', decent: '75mg', high: '100mg', unknown: '0mg' };
  var PF_BOOSTS = ['Beta-Alanine', 'CoQ10'];               // not Creatine
  var PF_DEFAULT_FLAVOUR = 'Lemon Lime';                   // also for Unflavored: there is no Unflavored Premium Fuel

  // ─── HELPERS ─────────────────────────────────────────────────────

  /**
   * Premium Fuel electrolyte level: 500mg is the average. Wringing Wet sweat
   * or Noticeably Salty sweat moves it to 1000mg; a Light Glow to 400mg.
   */
  function premiumFuelElectrolytes(answers) {
    var sweat = snapTo(answers.q5_sweat_level, SWEAT_ELECTROLYTE);
    var salt = snapTo(answers.q7_sweat_saltiness, SALT_ADJ);
    if (sweat === 100 || salt === 100) return '1000mg';
    if (sweat === 0) return '400mg';
    return '500mg';
  }

  /** Snap a raw slider value to the nearest key in a lookup table */
  function snapTo(value, table) {
    var v = Number(value) || 0;
    var keys = Object.keys(table).map(Number).sort(function (a, b) { return a - b; });
    var best = keys[0];
    var bestDist = Math.abs(v - best);
    for (var i = 1; i < keys.length; i++) {
      var d = Math.abs(v - keys[i]);
      if (d < bestDist) { best = keys[i]; bestDist = d; }
    }
    return best;
  }

  /** Calculate age from DOB string (YYYY-MM-DD) */
  function calculateAge(dob) {
    if (!dob) return 0;
    var parts = dob.split('-');
    var birth = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    var now = new Date();
    var age = now.getFullYear() - birth.getFullYear();
    var m = now.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) age--;
    return age;
  }

  /** Resolve body fat percentage from answers */
  function resolveBodyFat(answers) {
    var sex = answers.q11_sex || 'prefer_not';
    var cat = answers.q12_body_fat_category;

    // Exact % entered
    if (cat === 'exact' && answers.q13_body_fat_exact) {
      return Number(answers.q13_body_fat_exact);
    }

    // "I don't know" → use body type vibe
    if (cat === 'dont_know') {
      var vibe = answers.q14_body_type_vibe;
      if (sex === 'male') return BODY_TYPE_MALE[vibe] || 20;
      return BODY_TYPE_FEMALE[vibe] || 30;
    }

    // Category lookup
    if (sex === 'male') return BODY_FAT_MALE[cat] || 20;
    return BODY_FAT_FEMALE[cat] || 30;
  }

  /** Compute cumulative electrolyte slider, capped by sex */
  function computeElectrolytes(answers) {
    var base = SWEAT_ELECTROLYTE[snapTo(answers.q5_sweat_level, SWEAT_ELECTROLYTE)] || 35;
    var salt = SALT_ADJ[snapTo(answers.q7_sweat_saltiness, SALT_ADJ)] || 0;
    var cramp = CRAMP_ADJ[snapTo(answers.q8_cramp_frequency, CRAMP_ADJ)] || 0;

    var regionKey = answers.q15_region || '';
    var regionAdj = REGION_ADJ[regionKey] || 0;
    if (answers.q16_workout_location === 'inside') regionAdj = 0;

    var total = base + salt + cramp + regionAdj;

    var cap = (answers.q11_sex === 'male') ? 85 : 65;
    return Math.min(total, cap);
  }

  /**
   * Resolve protein type based on dietary prefs.
   * Returns { hydration: 'Whey'|'Vegan', protein: 'Whey'|'Vegan' }
   * Rule: if ANY selection results in Vegan for either, Vegan wins overall
   *       when "vegan" is explicitly selected it locks both to Vegan.
   */
  function resolveProteinType(answers) {
    var prefs = answers.q20_dietary_prefs || [];
    if (!Array.isArray(prefs)) prefs = [prefs];

    var result = { hydration: 'Whey', protein: 'Whey' };

    // "none" or empty → Whey/Whey
    if (!prefs.length || (prefs.length === 1 && prefs[0] === 'none')) {
      return result;
    }

    // Check each pref
    for (var i = 0; i < prefs.length; i++) {
      var p = prefs[i];
      if (p === 'dairy_free') {
        result.hydration = 'Vegan';
        result.protein = 'Vegan';
      } else if (p === 'soy_free') {
        result.hydration = 'Vegan';
        // protein stays Whey unless overridden
      } else if (p === 'plant_based') {
        result.hydration = result.hydration; // no hydration change from plant_based alone
        result.protein = 'Vegan';
      } else if (p === 'vegan') {
        // Strictly vegan overrides everything
        result.hydration = 'Vegan';
        result.protein = 'Vegan';
      }
      // gluten_free → no effect
    }

    // Override rule: if ANY path resulted in Vegan for either, lock both to Vegan
    if (prefs.indexOf('vegan') !== -1) {
      result.hydration = 'Vegan';
      result.protein = 'Vegan';
    }

    return result;
  }

  /** Resolve the primary event name from whichever sport sub-field is active */
  function resolvePrimaryEvent(answers) {
    var sport = answers.q1_sport_focus;
    var fieldMap = {
      triathlon: 'q1_triathlon_primary_event',
      cycling:   'q1_cycling_primary_event',
      running:   'q1_running_primary_event',
      swimming:  'q1_swimming_primary_event',
      strength:  'q1_strength_primary_event',
      paddling:  'q1_paddling_primary_event'
    };
    var field = fieldMap[sport];
    return (field && answers[field]) ? answers[field] : '';
  }

  /** Find a boost by name (case-insensitive partial match) in a boost catalog array */
  function findBoost(catalog, name) {
    if (!catalog || !catalog.length) return null;
    var target = name.toLowerCase();
    for (var i = 0; i < catalog.length; i++) {
      var bName = (catalog[i].label || catalog[i].name || catalog[i].title || '').toLowerCase();
      if (bName.indexOf(target) !== -1) return catalog[i];
    }
    return null;
  }

  /** Add a boost ID to an array (if found in catalog), avoiding duplicates */
  function addBoost(boostIds, catalog, boostName) {
    var b = findBoost(catalog, boostName);
    if (b && b.id && boostIds.indexOf(b.id) === -1) {
      boostIds.push(b.id);
    }
  }

  /** True when the named boost actually landed in the formula */
  function boostApplied(boostIds, catalog, boostName) {
    var b = findBoost(catalog, boostName);
    return !!(b && b.id && boostIds.indexOf(b.id) !== -1);
  }

  /**
   * Join boost names for prose. Normally "a and b", but a name that already
   * contains "and" (Turmeric and Black Pepper) is moved last and the list is
   * comma-joined, so the sentence never reads "turmeric and black pepper and
   * spirulina".
   */
  function joinNames(names) {
    if (names.length < 2) return names[0] || '';
    var plain = names.filter(function (n) { return n.indexOf(' and ') === -1; });
    var compound = names.filter(function (n) { return n.indexOf(' and ') !== -1; });
    if (compound.length) return plain.concat(compound).join(', ');
    return names.slice(0, -1).join(', ') + ' and ' + names[names.length - 1];
  }

  // ─── MAIN SCORING ────────────────────────────────────────────────

  /**
   * Score the quiz answers and return formula configs + recommendations.
   *
   * @param {Object} answers         — collected quiz answers
   * @param {Array}  hydrationBoosts — boost catalog from /apps/formulas?route=hydration-nutrition&mode=boosts
   * @param {Array}  proteinBoosts   — boost catalog from /apps/formulas?route=protein-nutrition&mode=boosts
   * @returns {Object} { hydrationA, hydrationB|null, protein, recommendations, resultsText, firstName, formulaNames }
   */
  function score(answers, hydrationBoosts, proteinBoosts) {
    var hBoosts = hydrationBoosts || [];
    var pBoosts = proteinBoosts || [];

    var firstName = (answers['customer[first_name]'] || '').trim() || 'Athlete';
    var workoutLength = answers.q2_workout_length || '1_hour';
    var is3Plus = workoutLength === '3_plus_hours';
    var sport = answers.q1_sport_focus || '';

    // Detect ultra-marathon
    var runningTypes = answers.q1_running_type || [];
    if (!Array.isArray(runningTypes)) runningTypes = [runningTypes];
    var isUltra = runningTypes.indexOf('ultra_marathon') !== -1;

    // Detect strength subtypes for boost additions
    var strengthTypes = answers.q1_strength_type || [];
    if (!Array.isArray(strengthTypes)) strengthTypes = [strengthTypes];
    var hasStrengthBoosts = sport === 'strength' &&
      (strengthTypes.indexOf('crossfit') !== -1 ||
       strengthTypes.indexOf('weight_lifting') !== -1 ||
       strengthTypes.indexOf('hyrox') !== -1);

    // ── Electrolytes ──
    var electrolytes = computeElectrolytes(answers);

    // ── Body composition ──
    // AUS asks in kg (TW 40087473). The formulas below are calibrated in lb, so
    // convert; q10_weight_lbs still works for submissions saved before the change.
    var weightKg = Number(answers.q10_weight_kg);
    var weight = weightKg > 0 ? weightKg * 2.20462 : (Number(answers.q10_weight_lbs) || 165);
    var bodyFatPct = resolveBodyFat(answers);
    var lbw = weight * (1 - bodyFatPct / 100);
    console.log('[quiz-engine] Body composition debug:',
      'q10_weight_lbs=' + JSON.stringify(answers.q10_weight_lbs),
      '→ weight=' + weight,
      '| q12_body_fat_category=' + JSON.stringify(answers.q12_body_fat_category),
      '| q13_body_fat_exact=' + JSON.stringify(answers.q13_body_fat_exact),
      '→ bodyFatPct=' + bodyFatPct,
      '→ lbw=' + lbw.toFixed(2),
      '| shortMix calories_slider=' + Math.max(0, Math.min(100, Math.round(((lbw * 1.6) - 100) / 226 * 100)))
    );

    // ── Age ──
    var age = calculateAge(answers.q9_birth_date);
    var isOver40 = age >= 40;

    // ── Protein type ──
    var proteinTypes = resolveProteinType(answers);
    var isVegan = proteinTypes.protein === 'Vegan';

    // ── Flavor ──
    var flavorVibe = answers.q18_flavor_vibe || 'fruit_berry';
    var flavors = FLAVOR_MAP[flavorVibe] || FLAVOR_MAP.fruit_berry;
    var flavorSlider = Number(answers.q17_flavor_strength) || 50;

    // ── Caffeine ──
    var caffPref = answers.q19_caffeine_pref || 'none';
    var caffeine = CAFFEINE_MAP[caffPref] || CAFFEINE_MAP.none;

    // ── Primary event ──
    var primaryEvent = resolvePrimaryEvent(answers);

    // ── Formula names ──
    var formulaNames = {};
    if (is3Plus) {
      formulaNames.a = firstName + "'s Short Mix";
      formulaNames.b = firstName + "'s Long Mix";
    } else if (primaryEvent) {
      formulaNames.a = firstName + "'s " + primaryEvent + " Formula";
    } else {
      formulaNames.a = firstName + "'s Custom Hydration";
    }
    formulaNames.protein = firstName + "'s Custom Protein";

    // ── Salt boost check ──
    var saltSnap = snapTo(answers.q7_sweat_saltiness, SALT_ADJ);
    var needsSodiumCitrate = saltSnap === 100;

    // ── Build Hydration Formula A ──
    var hABoosts = [];

    // Strength boosts
    if (hasStrengthBoosts) {
      addBoost(hABoosts, hBoosts, 'Creatine');
      addBoost(hABoosts, hBoosts, 'Beta-Alanine');
    }

    // Sodium Citrate if salt lick status
    if (needsSodiumCitrate) {
      addBoost(hABoosts, hBoosts, 'Sodium Citrate');
    }

    var hydrationA = {
      type: 'h',
      flavor: flavors.a,
      flavor_slider: flavorSlider,
      electrolytes_slider: electrolytes,
      calories_slider: Math.max(0, Math.min(100, Math.round(((lbw * 1.6) - 100) / 226 * 100))),
      carbs_slider: is3Plus ? 20 : 50,
      aminoacids_slider: 0,
      caffeine_slider: caffeine.a,
      protein_slider: 0,
      boosts: hABoosts
    };

    // ── Build Hydration Formula B (only if 3+ hours) ──
    var hydrationB = null;
    if (is3Plus) {
      var hBBoosts = [];

      if (hasStrengthBoosts) {
        addBoost(hBBoosts, hBoosts, 'Creatine');
        addBoost(hBBoosts, hBoosts, 'Beta-Alanine');
      }
      if (needsSodiumCitrate) {
        addBoost(hBBoosts, hBoosts, 'Sodium Citrate');
      }

      // Hunger → protein + amino acids on Formula B
      var hungerSnap = snapTo(answers.q2_hunger, HUNGER_PROTEIN);
      var hungerTable = isUltra ? HUNGER_ULTRA : HUNGER_PROTEIN;
      var hungerVals = hungerTable[hungerSnap] || { ps: 10, aa: 10 };

      hydrationB = {
        type: 'h',
        flavor: flavors.b,
        flavor_slider: flavorSlider,
        electrolytes_slider: electrolytes,
        calories_slider: Math.max(63, Math.min(100, Math.round(((lbw * 2.0) - 100) / 226 * 100))),
        carbs_slider: 80,
        aminoacids_slider: hungerVals.aa,
        caffeine_slider: caffeine.b,
        protein_slider: hungerVals.ps,
        protein_type: proteinTypes.hydration,
        boosts: hBBoosts
      };
    }

    // ── Build Protein Formula ──
    var pBoostIds = [];

    // Strength boosts for protein
    if (hasStrengthBoosts) {
      addBoost(pBoostIds, pBoosts, 'Creatine');
    }

    // Age 40+ → Double Protein + CoQ10
    if (isOver40) {
      addBoost(pBoostIds, pBoosts, 'Double Protein');
      addBoost(pBoostIds, pBoosts, 'CoQ10');
    }

    // q22 protein use cases
    var useCases = answers.q22_protein_use_cases || [];
    if (!Array.isArray(useCases)) useCases = [useCases];

    if (useCases.indexOf('meal_replacement') !== -1) {
      addBoost(pBoostIds, pBoosts, 'Double Protein');
      addBoost(pBoostIds, pBoosts, 'Berry Antioxidant'); // no-op after AUS boost cutover
      addBoost(pBoostIds, pBoosts, 'Turmeric and Black Pepper');
      addBoost(pBoostIds, pBoosts, 'Flaxseed');
      addBoost(pBoostIds, pBoosts, 'MCT Oil');
    }

    // Tart Cherry is never recommended by the quiz (Nathan, 2026-10-13: too tart for
    // a first-timer). Every path that used to add it adds Tryptophan instead, and
    // customers can still pick Tart Cherry themselves when editing a protein blend.

    // Post-workout → Repair Blend (unless Vegan), +Tryptophan, +Glutamine
    var needsRepairBlend = false;
    if (useCases.indexOf('post_workout') !== -1) {
      if (!isVegan) needsRepairBlend = true;
      addBoost(pBoostIds, pBoosts, 'Tryptophan');
      addBoost(pBoostIds, pBoosts, 'Glutamine');
      addBoost(pBoostIds, pBoosts, 'Creatine'); // AUS, TW 40087473
    }

    // Wellness (AUS, TW 40087473): Collagen, Creatine and Double Protein.
    // addBoost is a no-op for a boost the catalog does not have, so Collagen
    // takes effect once a Collagen boost exists in the AUS protein extras.
    if (useCases.indexOf('wellness') !== -1) {
      addBoost(pBoostIds, pBoosts, 'Collagen');
      addBoost(pBoostIds, pBoosts, 'Creatine');
      addBoost(pBoostIds, pBoosts, 'Double Protein');
    }

    if (useCases.indexOf('snack') !== -1) {
      addBoost(pBoostIds, pBoosts, 'Berry Antioxidant'); // no-op after AUS boost cutover
      addBoost(pBoostIds, pBoosts, 'Turmeric and Black Pepper');
      addBoost(pBoostIds, pBoosts, 'Flaxseed');
    }

    if (useCases.indexOf('before_bed') !== -1) {
      if (!isVegan) needsRepairBlend = true;
      addBoost(pBoostIds, pBoosts, 'Tryptophan');
      addBoost(pBoostIds, pBoosts, 'Flaxseed');
    }

    // Repair Blend is a protein_type selection, not a boost
    var proteinType = proteinTypes.protein;
    if (needsRepairBlend && !isVegan) {
      proteinType = 'Repair Blend';
    }

    // q24 game plan → calorie/carbs slider + boosts
    var gamePlan = answers.q24_game_plan || 'maintain';
    var calorieCarbsSlider = 50;
    if (gamePlan === 'lose') {
      calorieCarbsSlider = 1;
    } else if (gamePlan === 'gain') {
      calorieCarbsSlider = 100;
      addBoost(pBoostIds, pBoosts, 'Double Protein');
      addBoost(pBoostIds, pBoosts, 'MCT Oil');
      addBoost(pBoostIds, pBoosts, 'Creatine'); // AUS, TW 40087473
    }

    // q25 fruits & veggies
    // Q25 is paused on AUS. Only act on an actual answer: defaulting a missing
    // one to 0 would add the micronutrient boosts to every plan once they are
    // switched on.
    var fvAnswered = answers.q25_fruits_veggies !== undefined && answers.q25_fruits_veggies !== '';
    var fvSnap = Number(answers.q25_fruits_veggies) || 0;
    if (!fvAnswered) {
      // not asked
    } else if (fvSnap === 0) {
      addBoost(pBoostIds, pBoosts, 'Greens Blend');      // no-op after AUS boost cutover
      addBoost(pBoostIds, pBoosts, 'Spirulina');
      addBoost(pBoostIds, pBoosts, 'Berry Antioxidant'); // no-op after AUS boost cutover
      addBoost(pBoostIds, pBoosts, 'Turmeric and Black Pepper');
    } else if (fvSnap < 100) {
      addBoost(pBoostIds, pBoosts, 'Berry Antioxidant'); // no-op after AUS boost cutover
      addBoost(pBoostIds, pBoosts, 'Turmeric and Black Pepper');
    }

    // q26 soreness
    var sorenessSnap = snapTo(answers.q26_soreness, { 0: 0, 25: 0, 50: 1, 75: 1, 100: 2 });
    if (sorenessSnap === 50 || sorenessSnap === 75) {
      addBoost(pBoostIds, pBoosts, 'Tryptophan');
    } else if (sorenessSnap === 100) {
      addBoost(pBoostIds, pBoosts, 'Double Protein');
      addBoost(pBoostIds, pBoosts, 'Tryptophan');
      addBoost(pBoostIds, pBoosts, 'Glutamine');
    }

    // q27 workout time
    var workoutTime = answers.q27_workout_time || 'morning';
    if (workoutTime === 'night') {
      addBoost(pBoostIds, pBoosts, 'Tryptophan');
      // OVERRIDE: Formula A caffeine = 0
      hydrationA.caffeine_slider = 0;
    }

    var protein = {
      type: 'p',
      flavor: 'Chocolate',
      flavor_slider: 90,
      calorie_carbs_slider: calorieCarbsSlider,
      protein_type: proteinType,
      boosts: pBoostIds
    };

    // ── Recommendations ──
    var recommendations = [];

    // Athlete level → Premium Fructose Fuel
    var athleteSnap = snapTo(answers.q4_athlete_level, { 0: 0, 25: 0, 50: 0, 75: 1, 100: 2 });
    var premiumFuel = null;
    var pfFructose = PF_FRUCTOSE[athleteSnap];
    if (athleteSnap === 50 && weight > PF_MIDDLE_HEAVY_KG * 2.20462) pfFructose = PF_MIDDLE_HEAVY;
    if (pfFructose) {
      recommendations.push({ product: 'Premium Fuel', variant: pfFructose });
      // Shown alongside the hydration formulas, not instead of them. The
      // flavour follows Formula A; the results page falls back to Lemon Lime
      // when Premium Fuel does not come in it.
      premiumFuel = {
        type: 'cf',
        fructose: pfFructose,
        flavour: flavorVibe === 'unflavored' ? PF_DEFAULT_FLAVOUR : flavors.a,
        default_flavour: PF_DEFAULT_FLAVOUR,
        caffeine_amount: PF_CAFFEINE[caffPref] || '0mg',
        electrolyte_level: premiumFuelElectrolytes(answers),
        boost_labels: PF_BOOSTS.slice()
      };
      formulaNames.premiumFuel = firstName + "'s Premium Fuel";
    }

    // Sweat rate data → nutrition coaching
    if (answers.q6_sweat_rate_data === 'yes') {
      recommendations.push({ product: 'Nutrition Coaching', variant: 'Single Session' });
    } else {
      recommendations.push({ product: 'Nutrition Coaching', variant: 'Package' });
    }

    // Workout time → JAVI / NOCTURNE
    if (workoutTime === 'morning') {
      recommendations.push({ product: 'Mocha Protein' }); // was JAVI (AUS, TW 40087473)
      recommendations.push({ product: 'COLD BREW' });
    } else if (workoutTime === 'night') {
      recommendations.push({ product: 'NOCTURNE' });
    }

    // ── Results text (per-formula) ──

    // Shared hydration preamble (fueling habits)
    var hydrationPreamble = [];
    var habits = answers.q3_fueling_habits || [];
    if (!Array.isArray(habits)) habits = [habits];
    if (habits.indexOf('none') !== -1 || habits.length === 0) {
      hydrationPreamble.push('Your Custom Fueling plan replaces the need for any additional fuel, hydration, and recovery supplements before, during, and after workouts.');
    } else {
      var habitLabels = {
        preworkout: 'Preworkout',
        gels: 'Gels',
        chews: 'Chews',
        bars: 'Bars',
        salt_pills: 'Salt Pills',
        real_food: 'Real Food',
        drink_mix: 'Drink Mix'
      };
      var items = habits
        .map(function (h) { return habitLabels[h] || ''; })
        .filter(Boolean);
      if (items.length) {
        hydrationPreamble.push('Your Custom Fueling plan replaces the need for any ' + items.join(', ') + '.');
      }
    }

    // Hydration A description
    var hydrationAText = hydrationPreamble.slice();
    hydrationAText.push('Based on your estimated lean body mass and typical training duration, we set this formula at {{calories}} calories per hour to provide steady energy for workouts under 3 hours.');
    hydrationAText.push('Factoring in your training environment, sweat rate, salt loss, and cramping tendencies, we set your formula to provide {{sodium}} mg of sodium per hour to support hydration and performance.');
    if (hydrationA.caffeine_slider > 0) {
      hydrationAText.push('Based on your caffeine preferences, we added {{caffeine}} mg of caffeine per serving to help support focus and sustained performance.');
    }

    // Hydration B description (only if 3+ hours)
    var hydrationBText = [];
    if (is3Plus && hydrationB) {
      hydrationBText = hydrationPreamble.slice();
      hydrationBText.push('Based on your estimated lean body mass and longer training sessions, this formula delivers {{calories}} calories per hour to help sustain energy during efforts lasting 3 hours or more.');
      hydrationBText.push('Factoring in your training environment, sweat rate, salt loss, and cramping tendencies, we set your formula to provide {{sodium}} mg of sodium per hour to support hydration and performance.');
      if (hydrationB.protein_slider > 0) {
        hydrationBText.push('Because you regularly complete longer (3+ hour) efforts, we included {{protein}} grams of protein plus BCAAs to help curb hunger and support muscle repair \u2014 eliminating the need for additional fuel sources.');
      }
      if (hydrationB.caffeine_slider > 0) {
        hydrationBText.push('Based on your caffeine preferences, we added {{caffeine}} mg of caffeine per serving to help support focus and sustained performance.');
      }
    }

    // Protein description
    var proteinText = [];
    proteinText.push('Based on your typical training duration and goals, we set your protein blend at {{calories}} calories per serving to support recovery and daily fueling needs.');
    proteinText.push('With {{protein}} grams of protein per serving, this formula is designed to support muscle repair, recovery, and the maintenance of lean muscle mass.');

    // Which micronutrient boosts actually landed. AUS pauses Greens Blend and
    // Berry Antioxidant Blend until their replacements are approved, and swaps
    // them for Spirulina / Turmeric and Black Pepper, so the copy is built from
    // the catalog rather than assuming a fixed pair. Naming a boost the formula
    // does not contain is a nutrition claim we cannot stand behind.
    var greensName = boostApplied(pBoostIds, pBoosts, 'Greens Blend') ? 'greens'
      : boostApplied(pBoostIds, pBoosts, 'Spirulina') ? 'spirulina'
      : '';
    var berryName = boostApplied(pBoostIds, pBoosts, 'Berry Antioxidant') ? 'berry antioxidants'
      : boostApplied(pBoostIds, pBoosts, 'Turmeric and Black Pepper') ? 'turmeric and black pepper'
      : '';
    var flaxName = boostApplied(pBoostIds, pBoosts, 'Flaxseed') ? 'flaxseed' : '';

    // Fruits & veggies (q25)
    var fvVal = Number(answers.q25_fruits_veggies) || 0;
    var microNames = [berryName, greensName].filter(Boolean);
    if (!fvAnswered) {
      // Q25 not asked (paused on AUS): say nothing about fruit and veg intake.
    } else if (fvVal === 0 && microNames.length) {
      proteinText.push('Because your typical fruit and vegetable intake is low, we included ' + joinNames(microNames) + ' to help provide additional micronutrient support.');
    } else if (fvVal > 0 && fvVal < 100 && berryName) {
      proteinText.push('Since your daily fruit and vegetable intake is moderate, we added ' + berryName + ' to help support recovery and overall nutrition.');
    }

    // Soreness (q26) - map 0-100 slider to 1-5 scale
    var sorenessRaw = Number(answers.q26_soreness) || 0;
    var sorenessScale = Math.round(sorenessRaw / 25) + 1; // 0→1, 25→2, 50→3, 75→4, 100→5
    if (sorenessScale >= 3 && sorenessScale <= 4) {
      proteinText.push('You reported moderate muscle soreness (' + sorenessScale + ' out of 5), so we added tryptophan to help support rest and recovery.');
    } else if (sorenessScale >= 5) {
      proteinText.push('Since you reported high muscle soreness (5 out of 5), we increased your protein and added tryptophan and glutamine to provide additional recovery support.');
    }

    // Protein use cases (q22)
    var useCasesForText = answers.q22_protein_use_cases || [];
    if (!Array.isArray(useCasesForText)) useCasesForText = [useCasesForText];

    if (useCasesForText.indexOf('meal_replacement') !== -1) {
      var mealNames = [flaxName, boostApplied(pBoostIds, pBoosts, 'MCT Oil') ? 'MCT oil' : ''].filter(Boolean);
      if (mealNames.length) {
        proteinText.push('Because you plan to use this as a meal replacement, we increased protein and added ' + joinNames(mealNames) + ' to provide a balanced source of healthy fats, fiber, and sustained energy.');
      } else {
        proteinText.push('Because you plan to use this as a meal replacement, we increased protein to provide a balanced source of sustained energy.');
      }
    }
    if (useCasesForText.indexOf('post_workout') !== -1) {
      proteinText.push('Since you\'ll be using this post-workout, we included tryptophan and glutamine to help support muscle repair and recovery.');
    }
    if (useCasesForText.indexOf('snack') !== -1) {
      var snackNames = [flaxName, berryName].filter(Boolean);
      if (snackNames.length) {
        proteinText.push('Because you\'ll be using this as a snack, we added ' + joinNames(snackNames) + ' to provide extra nutritional support between meals.');
      }
    }
    if (useCasesForText.indexOf('before_bed') !== -1) {
      proteinText.push('Since you plan to use this before bed, we included tryptophan to help support recovery and promote more restful sleep.');
    }

    return {
      hydrationA: hydrationA,
      hydrationB: hydrationB,
      protein: protein,
      premiumFuel: premiumFuel,
      recommendations: recommendations,
      resultsText: [],
      hydrationAText: hydrationAText,
      hydrationBText: hydrationBText,
      proteinText: proteinText,
      firstName: firstName,
      formulaNames: formulaNames,
      lbw: Math.round(lbw),
      is3Plus: is3Plus
    };
  }

  // ─── BOOST FETCHING + SCORE ──────────────────────────────────────

  /**
   * Fetch boost catalogs from the app proxy, then score.
   * @param {Object} answers
   * @returns {Promise<Object>} scored results
   */
  function fetchBoosts(route) {
    return fetch('/apps/formulas?route=' + route + '&mode=boosts')
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (data) {
        return (data && data.boosts) ? data.boosts : [];
      })
      .catch(function (err) {
        console.warn('[quiz-engine] Failed to load ' + route + ' boosts:', err);
        return [];
      });
  }

  function fetchAndScore(answers) {
    return Promise.all([
      fetchBoosts('hydration-nutrition'),
      fetchBoosts('protein-nutrition')
    ]).then(function (results) {
      return score(answers, results[0], results[1]);
    });
  }

  // Public API
  return {
    score: score,
    fetchAndScore: fetchAndScore
  };
})();
